import React, { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Copy } from "lucide-react";
import { api, formatApiError, formatPrice, imgUrl } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import ImagePicker from "./ImagePicker";
import { OctopusMascot } from "@/components/Mascot";

const EMPTY_PRODUCT = {
  name: "", description: "", price: 0, promo_price: "", category_id: "",
  images: [], colors: [], variations: [], available: true, featured: false, badge: "",
};

export default function AdminProducts() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // product being edited or null
  const [form, setForm] = useState(EMPTY_PRODUCT);
  const [colorsText, setColorsText] = useState("");
  const [variationsText, setVariationsText] = useState("");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: prods }, { data: cats }] = await Promise.all([
        api.get("/products", { params: { sort: "newest" } }),
        api.get("/categories", { params: { all: "true" } }),
      ]);
      setProducts(prods);
      setCategories(cats);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openNew = () => {
    setForm({ ...EMPTY_PRODUCT });
    setColorsText("");
    setVariationsText("");
    setEditing("new");
  };

  const openEdit = (p) => {
    setForm({ ...EMPTY_PRODUCT, ...p, promo_price: p.promo_price || "" });
    setColorsText((p.colors || []).join(", "));
    setVariationsText((p.variations || []).join(", "));
    setEditing(p.id);
  };

  const duplicate = (p) => {
    setForm({ ...EMPTY_PRODUCT, ...p, name: `${p.name} (cópia)`, id: undefined, promo_price: p.promo_price || "" });
    setColorsText((p.colors || []).join(", "));
    setVariationsText((p.variations || []).join(", "));
    setEditing("new");
  };

  const save = async () => {
    if (!form.name.trim()) {
      toast.error("Dê um nome ao produto");
      return;
    }
    setSaving(true);
    const payload = {
      ...form,
      price: Number(form.price) || 0,
      promo_price: form.promo_price === "" || form.promo_price === null ? null : Number(form.promo_price) || null,
      colors: colorsText.split(",").map((s) => s.trim()).filter(Boolean),
      variations: variationsText.split(",").map((s) => s.trim()).filter(Boolean),
    };
    delete payload.id;
    delete payload.created_at;
    try {
      if (editing === "new") {
        await api.post("/admin/products", payload);
        toast.success("Produto criado!");
      } else {
        await api.put(`/admin/products/${editing}`, payload);
        toast.success("Produto atualizado!");
      }
      setEditing(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Excluir "${p.name}"? Essa ação não pode ser desfeita.`)) return;
    try {
      await api.delete(`/admin/products/${p.id}`);
      toast.success("Produto excluído");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const catName = (id) => categories.find((c) => c.id === id)?.name || "—";

  return (
    <div data-testid="admin-products-tab">
      <div className="flex items-center justify-between mb-5">
        <h2 className="text-lg font-black text-slate-800">Produtos ({products.length})</h2>
        <button onClick={openNew} data-testid="new-product-btn" className="bg-brand-gradient text-white text-sm font-bold px-4 py-2.5 rounded-full inline-flex items-center gap-1.5 shadow-soft active:scale-95 transition-transform">
          <Plus size={16} /> Novo produto
        </button>
      </div>

      {loading ? (
        <div className="space-y-3">{Array.from({ length: 4 }).map((_, i) => <div key={i} className="h-16 bg-slate-100 rounded-2xl animate-pulse" />)}</div>
      ) : products.length === 0 ? (
        <div className="text-center py-14 flex flex-col items-center gap-3">
          <OctopusMascot size={80} />
          <p className="text-slate-500 text-sm">Nenhum produto ainda. Crie o primeiro!</p>
        </div>
      ) : (
        <div className="space-y-3">
          {products.map((p) => (
            <div key={p.id} className="bg-white rounded-2xl shadow-card p-3 flex items-center gap-3" data-testid={`admin-product-row-${p.id}`}>
              <div className="w-14 h-14 rounded-xl overflow-hidden bg-slate-100 shrink-0">
                {p.images?.[0] ? <img src={imgUrl(p.images[0])} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full bg-soft-gradient" />}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-bold text-slate-800 text-sm truncate">{p.name}</p>
                <p className="text-xs text-slate-400">
                  {catName(p.category_id)} · {formatPrice(p.price)}
                  {p.promo_price > 0 && <span className="text-pink-500 font-bold"> → {formatPrice(p.promo_price)}</span>}
                  {p.badge && <span className="ml-1.5 bg-purple-50 text-purple-600 px-1.5 py-0.5 rounded-full font-bold">{{ mais_vendido: "⭐", favorito: "💗", novidade: "✨" }[p.badge]}</span>}
                  {!p.available && <span className="ml-1.5 bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded-full font-bold">Esgotado</span>}
                </p>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <button onClick={() => duplicate(p)} aria-label="Duplicar" data-testid={`duplicate-product-${p.id}`} className="w-9 h-9 rounded-full hover:bg-purple-50 text-slate-400 hover:text-purple-500 flex items-center justify-center transition-colors"><Copy size={16} /></button>
                <button onClick={() => openEdit(p)} aria-label="Editar" data-testid={`edit-product-${p.id}`} className="w-9 h-9 rounded-full hover:bg-pink-50 text-slate-400 hover:text-pink-500 flex items-center justify-center transition-colors"><Pencil size={16} /></button>
                <button onClick={() => remove(p)} aria-label="Excluir" data-testid={`delete-product-${p.id}`} className="w-9 h-9 rounded-full hover:bg-red-50 text-slate-400 hover:text-red-500 flex items-center justify-center transition-colors"><Trash2 size={16} /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!editing} onOpenChange={(open) => !open && setEditing(null)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto rounded-3xl" data-testid="product-form-dialog">
          <DialogHeader>
            <DialogTitle className="font-black">{editing === "new" ? "Novo produto" : "Editar produto"}</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col gap-4 mt-2">
            <div>
              <label className="text-xs font-bold text-slate-600 mb-1.5 block">Nome</label>
              <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="product-form-name"
                className="w-full px-4 py-2.5 rounded-xl border border-slate-200 text-sm outline-none focus:border-pink-300" placeholder="Nome do produto" />
            </div>
            <div>
              <label className="text-xs font-bold text-slate-600 mb-1.5 block">Descrição</label>
              <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} data-testid="product-form-description"
                className="w-full px-4 py-2.5 rounded-xl border border-slate-200 text-sm outline-none focus:border-pink-300 resize-none" placeholder="Descrição curta do produto" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-bold text-slate-600 mb-1.5 block">Preço (R$)</label>
                <input type="number" min="0" step="0.01" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} data-testid="product-form-price"
                  className="w-full px-4 py-2.5 rounded-xl border border-slate-200 text-sm outline-none focus:border-pink-300" />
              </div>
              <div>
                <label className="text-xs font-bold text-slate-600 mb-1.5 block">Preço promocional</label>
                <input type="number" min="0" step="0.01" value={form.promo_price} onChange={(e) => setForm({ ...form, promo_price: e.target.value })} data-testid="product-form-promo-price"
                  className="w-full px-4 py-2.5 rounded-xl border border-slate-200 text-sm outline-none focus:border-pink-300" placeholder="Opcional" />
              </div>
            </div>
            <div>
              <label className="text-xs font-bold text-slate-600 mb-1.5 block">Categoria</label>
              <Select value={form.category_id || "none"} onValueChange={(v) => setForm({ ...form, category_id: v === "none" ? "" : v })}>
                <SelectTrigger className="rounded-xl" data-testid="product-form-category"><SelectValue placeholder="Selecione" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Sem categoria</SelectItem>
                  {categories.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <ImagePicker images={form.images} onChange={(imgs) => setForm({ ...form, images: imgs })} label="Fotos do produto" />
            <div>
              <label className="text-xs font-bold text-slate-600 mb-1.5 block">Cores disponíveis (separe por vírgula)</label>
              <input value={colorsText} onChange={(e) => setColorsText(e.target.value)} data-testid="product-form-colors"
                className="w-full px-4 py-2.5 rounded-xl border border-slate-200 text-sm outline-none focus:border-pink-300" placeholder="Rosa, Roxo, Azul" />
            </div>
            <div>
              <label className="text-xs font-bold text-slate-600 mb-1.5 block">Variações/opções (separe por vírgula)</label>
              <input value={variationsText} onChange={(e) => setVariationsText(e.target.value)} data-testid="product-form-variations"
                className="w-full px-4 py-2.5 rounded-xl border border-slate-200 text-sm outline-none focus:border-pink-300" placeholder="P, M, G" />
            </div>
            <div>
              <label className="text-xs font-bold text-slate-600 mb-1.5 block">Selo</label>
              <Select value={form.badge || "none"} onValueChange={(v) => setForm({ ...form, badge: v === "none" ? "" : v })}>
                <SelectTrigger className="rounded-xl" data-testid="product-form-badge"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Sem selo</SelectItem>
                  <SelectItem value="mais_vendido">⭐ Mais vendido</SelectItem>
                  <SelectItem value="favorito">💗 Favorito</SelectItem>
                  <SelectItem value="novidade">✨ Novidade</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center justify-between bg-slate-50 rounded-xl px-4 py-3">
              <span className="text-sm font-bold text-slate-700">Disponível para compra</span>
              <Switch checked={form.available} onCheckedChange={(v) => setForm({ ...form, available: v })} data-testid="product-form-available" />
            </div>
            <div className="flex items-center justify-between bg-slate-50 rounded-xl px-4 py-3">
              <span className="text-sm font-bold text-slate-700">Produto em destaque</span>
              <Switch checked={form.featured} onCheckedChange={(v) => setForm({ ...form, featured: v })} data-testid="product-form-featured" />
            </div>
            <button onClick={save} disabled={saving} data-testid="product-form-save"
              className="bg-brand-gradient text-white font-bold py-3.5 rounded-full shadow-soft active:scale-95 transition-transform disabled:opacity-60 mt-2">
              {saving ? "Salvando..." : "Salvar produto"}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
